<template>
    <b-modal
        id="modal-beta-welcome"
        ref="betaWelcomeModal"
        size="lg"
        modal-class="modal-beta-welcome-dim"
        dialog-class="modal-beta-welcome-dialog"
        no-close-on-backdrop
        no-close-on-esc
        hide-header-close
        header-class="border-0 p-0"
        body-class="p-0"
        footer-class="border-0 pt-0 pb-3 px-4 d-flex justify-content-between align-items-center flex-wrap"
        data-test="modal-beta-welcome"
        :data-state="actionState"
        :data-countdown="countdown"
        @show="handleModalShow"
        @shown="handleModalShown"
        @hidden="handleModalHidden"
    >
        <!-- Custom Header Banner -->
        <template #modal-header>
            <div class="beta-welcome-hero w-100 py-3 px-4 position-relative text-center">
                <div class="beta-welcome-badge mx-auto mb-2 shadow-sm">
                    <i class="bi bi-stars"></i>
                </div>
                <h5 class="font-weight-bold mb-1 welcome-title">
                    Welcome to the VelocyTrack Beta Community
                </h5>
                <p class="text-muted small mb-0 welcome-subtitle mx-auto">
                    A dedicated, long-term supported platform for engineers who love building and shaping the cutting edge.
                </p>
            </div>
        </template>

        <!-- Body Content: The 4 Value Pillars -->
        <div class="px-4 py-2 py-md-3">
            <b-row class="mb-2">
                <!-- Pillar 1: Dedicated LTS Instance -->
                <b-col md="6" class="mb-2">
                    <div class="welcome-pillar-card p-2.5 h-100 rounded border" data-test="card-pillar-lts">
                        <div class="d-flex align-items-center mb-1">
                            <div class="pillar-icon mr-2 text-primary">
                                <i class="bi bi-hdd-network-fill"></i>
                            </div>
                            <h6 class="font-weight-bold mb-0 text-dark pillar-heading">
                                Dedicated LTS Beta Instance
                            </h6>
                        </div>
                        <p class="text-muted small mb-0">
                            This beta instance is a distinct, long-term supported environment with continuous feature deployments and support for early adopters.
                        </p>
                    </div>
                </b-col>

                <!-- Pillar 2: 100% Free Forever -->
                <b-col md="6" class="mb-2">
                    <div class="welcome-pillar-card p-2.5 h-100 rounded border" data-test="card-pillar-free">
                        <div class="d-flex align-items-center mb-1">
                            <div class="pillar-icon mr-2 text-warning">
                                <i class="bi bi-gem"></i>
                            </div>
                            <h6 class="font-weight-bold mb-0 text-dark pillar-heading">
                                100% Free Access Forever
                            </h6>
                        </div>
                        <p class="text-muted small mb-0">
                            Enjoy unlimited Pro capabilities on this beta instance with zero subscription fees, for as long as you use it and help us improve. Beta members also get 50% off their first year after official release.
                        </p>
                    </div>
                </b-col>

                <!-- Pillar 3: Reciprocal Feedback Contract -->
                <b-col md="6" class="mb-2 mb-md-0">
                    <div class="welcome-pillar-card p-2.5 h-100 rounded border" data-test="card-pillar-feedback">
                        <div class="d-flex align-items-center mb-1">
                            <div class="pillar-icon mr-2 text-info">
                                <i class="bi bi-chat-heart-fill"></i>
                            </div>
                            <h6 class="font-weight-bold mb-0 text-dark pillar-heading">
                                Active Feedback Partnership
                            </h6>
                        </div>
                        <p class="text-muted small mb-0">
                            In return for free access, we kindly ask you to report bugs, share impressions, and validate new workflows through the in-app Help &amp; Feedback tools.
                        </p>
                    </div>
                </b-col>

                <!-- Pillar 4: Co-Creator Recognition -->
                <b-col md="6">
                    <div class="welcome-pillar-card p-2.5 h-100 rounded border" data-test="card-pillar-attribution">
                        <div class="d-flex align-items-center mb-1">
                            <div class="pillar-icon mr-2 text-success">
                                <i class="bi bi-award-fill"></i>
                            </div>
                            <h6 class="font-weight-bold mb-0 text-dark pillar-heading">
                                Co-Creator Attribution
                            </h6>
                        </div>
                        <p class="text-muted small mb-0">
                            Your contributions directly shape the roadmap. Bug fixes and suggestions are credited in release changelogs with celebratory resolution badges.
                        </p>
                    </div>
                </b-col>
            </b-row>

            <!-- Legal / Audit Notice & Explicit Consent Checkbox -->
            <div class="welcome-audit-notice py-2 px-3 rounded border text-muted small" data-test="box-beta-terms-consent">
                <b-form-checkbox
                    id="chk-agree-beta-terms"
                    v-model="agreedToTerms"
                    name="chk-agree-beta-terms"
                    data-test="chk-agree-beta-terms"
                    class="d-flex align-items-start"
                >
                    <span class="text-dark welcome-consent-label">
                        I acknowledge and agree to the <strong>Beta Terms of Service</strong> (v1.0.0-beta), confirm I am using this tool in my professional developer capacity, acknowledge the stateless on-the-fly execution model, and agree to participate in community feedback.
                        <a href="#" @click.prevent.stop="openLegal('terms')" class="text-primary font-weight-bold ml-1 text-decoration-none" data-test="link-beta-terms">
                            Terms of Service &amp; Privacy Policy &rarr;
                        </a>
                    </span>
                </b-form-checkbox>
            </div>
        </div>

        <!-- Modal Footer Actions -->
        <template #modal-footer>
            <div class="d-flex align-items-center justify-content-between w-100 flex-wrap">
                <b-button
                    variant="outline-danger"
                    size="sm"
                    class="mb-2 mb-sm-0"
                    :disabled="isSubmitting || isDeclining"
                    data-test="btn-decline-beta-welcome"
                    @click="declineAndExit"
                >
                    <span v-if="isDeclining" class="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                    <i v-else class="bi bi-box-arrow-right mr-1"></i>
                    Decline &amp; Exit
                </b-button>

                <b-button
                    variant="primary"
                    size="md"
                    class="font-weight-bold px-4 mb-2 mb-sm-0 shadow-sm"
                    :disabled="!agreedToTerms || countdown > 0 || isSubmitting || isDeclining"
                    data-test="btn-acknowledge-beta-welcome"
                    @click="acknowledgeWelcome"
                >
                    <span v-if="isSubmitting" class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                    <i v-else-if="countdown > 0" class="bi bi-hourglass-split mr-2"></i>
                    <i v-else class="bi bi-check2-circle mr-2"></i>
                    {{ submitButtonText }}
                </b-button>
            </div>
        </template>
    </b-modal>
</template>

<script>
    import eventBus from '../event-bus';
    import mixinToast from '../mixinToast';

    export default {
        name: 'ModalBetaWelcome',
        mixins: [mixinToast],
        data() {
            return {
                countdown: 10,
                timer: null,
                agreedToTerms: false,
                isSubmitting: false,
                isDeclining: false
            };
        },
        computed: {
            actionState() {
                if (this.isDeclining) return 'declining';
                if (this.isSubmitting) return 'submitting';
                if (this.countdown > 0) return 'counting';
                if (!this.agreedToTerms) return 'awaiting-consent';
                return 'ready';
            },
            submitButtonText() {
                if (this.isSubmitting) return 'Saving...';
                if (this.countdown > 0) return `Got It, Let's Build Together (${this.countdown}s)`;
                return "Got It, Let's Build Together!";
            }
        },
        mounted() {
            eventBus.$on('show-beta-welcome-modal', this.show);
        },
        beforeUnmount() {
            eventBus.$off('show-beta-welcome-modal', this.show);
            this.$store.commit('setBetaWelcomeOpen', false);
            this.clearTimer();
        },
        methods: {
            show() {
                this.agreedToTerms = false;
                this.$store.commit('setBetaWelcomeOpen', true);
                eventBus.$emit('beta-welcome-shown');
                if (this.$refs.betaWelcomeModal) {
                    this.$refs.betaWelcomeModal.show();
                }
            },
            hide() {
                if (this.$refs.betaWelcomeModal) {
                    this.$refs.betaWelcomeModal.hide();
                }
            },
            handleModalShow() {
                this.agreedToTerms = false;
                this.$store.commit('setBetaWelcomeOpen', true);
                eventBus.$emit('beta-welcome-shown');
            },
            handleModalShown() {
                this.$store.commit('setBetaWelcomeOpen', true);
                eventBus.$emit('beta-welcome-shown');
                this.countdown = 10;
                this.clearTimer();
                this.timer = setInterval(() => {
                    if (this.countdown > 0) {
                        this.countdown--;
                    } else {
                        this.clearTimer();
                    }
                }, 1000);
            },
            handleModalHidden() {
                this.$store.commit('setBetaWelcomeOpen', false);
                eventBus.$emit('beta-welcome-hidden');
                this.clearTimer();
            },
            clearTimer() {
                if (this.timer) {
                    clearInterval(this.timer);
                    this.timer = null;
                }
            },
            openLegal(tab) {
                eventBus.$emit('open-legal', tab || 'terms');
            },
            async declineAndExit() {
                if (this.isSubmitting || this.isDeclining) return;
                this.isDeclining = true;
                try {
                    await this.$http.post('/AccountConfig/DeclineBetaWelcome');
                } catch (e) {
                    // Ignore transient network errors on sign-out
                } finally {
                    this.clearTimer();
                    this.hide();
                    this.$store.commit('setBetaWelcomeOpen', false);
                    this.$store.commit('setAcknowledgedBetaWelcome', false);
                    this.$store.commit('signOut');
                    this.toastInfo('Exited System', 'You have declined the Beta Community terms and exited.');
                    if (this.$route.path !== '/') {
                        this.$router.push('/').catch(() => {});
                    }
                    this.isDeclining = false;
                }
            },
            async acknowledgeWelcome() {
                if (!this.agreedToTerms || this.countdown > 0 || this.isSubmitting || this.isDeclining) return;

                this.isSubmitting = true;
                try {
                    await this.$http.post('/AccountConfig/AcknowledgeBetaWelcome', {
                        termsAccepted: true,
                        termsVersionAccepted: '1.0.0-beta'
                    });
                    this.$store.commit('setAcknowledgedBetaWelcome', true);
                    this.$store.commit('setBetaUser', true);

                    const uid = this.$store.state.account?.uid || 'user';
                    try {
                        localStorage.setItem(`vt_beta_welcome_ack_${uid}`, 'true');
                    } catch (e) {
                        // ignore storage errors
                    }

                    // Attempt to fetch workspace now that account is initiated
                    try {
                        const wsResp = await this.$http.get('/Workspace');
                        if (wsResp && wsResp.data) {
                            this.$store.commit('setWorkspace', wsResp.data);
                        }
                    } catch (_) {
                        // ignore workspace fetch errors
                    }

                    this.toastSuccess('Welcome Aboard!', 'Thank you for partnering with us to build VelocyTrack!');
                    this.hide();
                } catch (err) {
                    this.toastError('Notice', 'Could not record acknowledgment, but you can continue exploring.');
                    this.$store.commit('setAcknowledgedBetaWelcome', true);
                    this.hide();
                } finally {
                    this.isSubmitting = false;
                }
            }
        }
    };
</script>

<style>
    /* Welcome Modal Deep Dimmed Backdrop & Elevated Positioning */
    .modal.modal-beta-welcome-dim {
        background-color: rgba(2, 6, 23, 0.86) !important;
        backdrop-filter: blur(8px) !important;
        -webkit-backdrop-filter: blur(8px) !important;
    }

    .modal.modal-beta-welcome-dim .modal-beta-welcome-dialog {
        margin: clamp(1rem, 2.5vh, 2rem) auto 1.5rem auto !important;
        max-width: 720px;
        width: 92vw;
    }

    .modal.modal-beta-welcome-dim .modal-content {
        border-radius: 0.65rem;
        overflow: hidden;
        border: 1px solid rgba(245, 158, 11, 0.35);
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.6), 0 0 40px rgba(245, 158, 11, 0.15);
    }
</style>

<style scoped>
    .beta-welcome-hero {
        background: linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, rgba(217, 119, 6, 0.04) 100%);
        border-bottom: 1px solid rgba(245, 158, 11, 0.2);
        border-top-left-radius: 0.6rem;
        border-top-right-radius: 0.6rem;
    }
    .beta-welcome-badge {
        width: 44px;
        height: 44px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.35rem;
        background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
        color: #ffffff;
        box-shadow: 0 4px 12px rgba(245, 158, 11, 0.35);
    }
    .welcome-title {
        color: #0f172a;
        font-size: 1.25rem;
        letter-spacing: -0.01em;
    }
    .welcome-subtitle {
        max-width: 520px;
        font-size: 0.82rem;
        line-height: 1.4;
    }
    .welcome-pillar-card {
        background-color: #f8fafc;
        border-color: #e2e8f0 !important;
        padding: 0.75rem 0.85rem !important;
        transition: transform 0.15s ease, box-shadow 0.15s ease;
    }
    .welcome-pillar-card p {
        font-size: 0.8rem;
        line-height: 1.35;
    }
    .welcome-pillar-card:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    }
    .pillar-icon {
        font-size: 1.1rem;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border-radius: 6px;
        background: rgba(148, 163, 184, 0.12);
    }
    .pillar-heading {
        font-size: 0.88rem;
    }
    .welcome-audit-notice {
        background-color: #f1f5f9;
        border-color: #cbd5e1 !important;
        font-size: 0.78rem;
        line-height: 1.35;
    }
    .font-size-lg {
        font-size: 1.25rem;
    }

    /* Dark Theme Overrides */
    [data-theme="dark"] .beta-welcome-hero {
        background: linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(15, 23, 42, 0.6) 100%);
        border-bottom-color: rgba(245, 158, 11, 0.3);
    }
    [data-theme="dark"] .welcome-title {
        color: #f8fafc;
    }
    [data-theme="dark"] .welcome-subtitle {
        color: #94a3b8 !important;
    }
    [data-theme="dark"] .welcome-pillar-card {
        background-color: #1e293b;
        border-color: #334155 !important;
    }
    [data-theme="dark"] .welcome-pillar-card:hover {
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
    }
    [data-theme="dark"] .pillar-heading {
        color: #f1f5f9 !important;
    }
    [data-theme="dark"] .welcome-audit-notice {
        background-color: #0f172a;
        border-color: #334155 !important;
        color: #94a3b8 !important;
    }
    [data-theme="dark"] .welcome-consent-label {
        color: #e2e8f0 !important;
    }
    [data-theme="dark"] .pillar-icon {
        background: rgba(255, 255, 255, 0.05);
    }
</style>
